import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Work as ProjectsIcon,
  AccountBalanceWallet as WalletIcon,
  CardGiftcard as RewardsIcon,
  HowToVote as GovernanceIcon,
  Assessment as PointsDistributionIcon,
  Assignment as BlogIcon,
  People as UsersIcon,
  Person as ProfileIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Description as FormsIcon,
  Analytics as PlaygroundIcon,
  PeopleAlt as FamilyIcon,
  Build as InfrastructureIcon,
  ReportProblem as ComplaintIcon,
  Schedule as WorkScheduleIcon,
  Museum as ExhibitionIcon,
  Schema as SchemaIcon,
  ShoppingCart as PardavimaiIcon,
  Contacts as ContactsIcon,
  Inventory as StorageIcon,
  Lock as LockersNavIcon,
  LocalDrink as StiklainisIcon,
  Settings as AdminIcon,
  Article as ContractsIcon,
  Description as DocumentIcon,
  Event as EventIcon,
  BeachAccess as BeachAccessIcon,
  Public as PlanetariumIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, getApiUrl } from '../config/api';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import FloatingDocumentation from './FloatingDocumentation';
import SchedulingCloseAnnouncementModal from './SchedulingCloseAnnouncementModal';
import webSocketService from '../services/WebSocketService';

const drawerWidth = 240;
const NAV_COLLAPSED_KEY = 'layout.navCollapsed';

function readNavCollapsed(): boolean {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Modules visible in nav but not yet launched — greyed out and non-clickable */
const NOT_LAUNCHED_PANELS = new Set([
  'mokymai',
  'projects',
  'wallet',
  'rewards',
  'governance',
  'infrastructure',
  'points-distribution',
  'skaistykla',
]);

interface LayoutProps {
  children: React.ReactNode;
  currentPage?: string;
  setCurrentPage?: (page: string) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, currentPage = 'dashboard', setCurrentPage }) => {
  console.log('🏗️ Layout component rendering. Current page:', currentPage);
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsed);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [visiblePanels, setVisiblePanels] = useState<string[]>([]);
  const [visibilityLoading, setVisibilityLoading] = useState(true);
  const [documentationOpen, setDocumentationOpen] = useState(false);
  const [unassignedExcursionCount, setUnassignedExcursionCount] = useState(0);
  const [pendingPlanetariumRequestCount, setPendingPlanetariumRequestCount] = useState(0);
  const [pendingTicketRequestCount, setPendingTicketRequestCount] = useState(0);
  const { user, logout, token, hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const canManageExcursions = hasPermission('excursions.manage');
  const canManagePlanetarium = hasPermission('planetarium.manage');
  const canApproveTicketRequests =
    hasPermission('family.manage') || hasPermission('family.approve');

  // Initialize WebSocket connection when user is authenticated
  useEffect(() => {
    if (token && user) {
      webSocketService.connect(token);
    } else {
      webSocketService.disconnect();
    }
  }, [token, user]);

  // Expired/invalid JWT on WebSocket → same as API 401: go to login
  useEffect(() => {
    const onAuthFailure = () => {
      logout();
    };
    webSocketService.on('auth-failure', onAuthFailure);
    return () => {
      webSocketService.off('auth-failure', onAuthFailure);
    };
  }, [logout]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavCollapseToggle = () => {
    setNavCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const desktopDrawerWidth = navCollapsed ? 0 : drawerWidth;

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleNavigationClick = (path: string) => {
    if (path === 'dokumentacija') {
      setDocumentationOpen(true);
    } else if (setCurrentPage) {
      setCurrentPage(path);
    }
  };

  // Fetch visible panels for current user role
  React.useEffect(() => {
    console.log('🚀 Layout useEffect triggered. User:', user?.firstName, 'Token exists:', !!token);
    
    const fetchVisiblePanels = async () => {
      if (!user || !token) {
        console.log('❌ No user or token, skipping visibility fetch');
        setVisibilityLoading(false);
        return;
      }

      try {
        console.log('🔍 Fetching panel visibility');
        // Use permission-based endpoint
        const response = await fetch(getApiUrl('/admin/panel-visibility/user'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Panel visibility response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('📊 Panel visibility data:', data);
          if (data.success) {
            console.log('✅ Setting visible panels:', data.visiblePanels);
            setVisiblePanels(data.visiblePanels || []);
          }
        } else {
          console.log('❌ Panel visibility API failed:', response.status);
        }
      } catch (error) {
        console.error('Error fetching panel visibility:', error);
        // If API fails, use minimal fallback (dashboard and profile only)
        console.log('🔄 Using minimal fallback - dashboard and profile only');
        setVisiblePanels(['dashboard', 'profile']);
      } finally {
        setVisibilityLoading(false);
      }
    };

    fetchVisiblePanels();
  }, [user, token]);

  useEffect(() => {
    if (!user || !token || !canManageExcursions) {
      setUnassignedExcursionCount(0);
      return;
    }

    let cancelled = false;
    const fetchUnassigned = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.EXCURSIONS_UNASSIGNED_COUNT), {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled && data.success) {
          setUnassignedExcursionCount(Number(data.count) || 0);
        }
      } catch {
        /* ignore */
      }
    };

    void fetchUnassigned();
    const intervalId = window.setInterval(() => void fetchUnassigned(), 5 * 60 * 1000);
    const onFocus = () => void fetchUnassigned();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, token, canManageExcursions]);

  useEffect(() => {
    if (!user || !token || !canManagePlanetarium) {
      setPendingPlanetariumRequestCount(0);
      return;
    }

    let cancelled = false;
    const fetchPendingRequests = async () => {
      try {
        const response = await fetch(
          getApiUrl(API_ENDPOINTS.PLANETARIUM_PRIVATE_REQUESTS_PENDING_COUNT),
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled && data.success) {
          setPendingPlanetariumRequestCount(Number(data.count) || 0);
        }
      } catch {
        /* ignore */
      }
    };

    void fetchPendingRequests();
    const intervalId = window.setInterval(() => void fetchPendingRequests(), 5 * 60 * 1000);
    const onFocus = () => void fetchPendingRequests();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, token, canManagePlanetarium]);

  useEffect(() => {
    if (!user || !token || !canApproveTicketRequests) {
      setPendingTicketRequestCount(0);
      return;
    }

    let cancelled = false;
    const fetchPendingTickets = async () => {
      try {
        const response = await fetch(
          getApiUrl(API_ENDPOINTS.TICKET_REQUESTS_PENDING_COUNT),
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled && data.success) {
          setPendingTicketRequestCount(Number(data.count) || 0);
        }
      } catch {
        /* ignore */
      }
    };

    void fetchPendingTickets();
    const intervalId = window.setInterval(() => void fetchPendingTickets(), 5 * 60 * 1000);
    const onFocus = () => void fetchPendingTickets();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, token, canApproveTicketRequests]);

  const handleNavigation = (page: string) => {
    if (page === 'dokumentacija') {
      setDocumentationOpen(true);
    } else if (setCurrentPage) {
      setCurrentPage(page);
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const mainMenuItems = [
    { text: t('navigation.mokymai', 'Mokymai'), icon: <DocumentIcon />, path: 'mokymai', adminOnly: false },
    { text: t('navigation.blog'), icon: <BlogIcon />, path: 'blog', adminOnly: false },
    { text: t('navigation.dashboard'), icon: <DashboardIcon />, path: 'dashboard', adminOnly: false },
    { text: t('navigation.projects'), icon: <ProjectsIcon />, path: 'projects', adminOnly: false },
    { text: t('navigation.wallet'), icon: <WalletIcon />, path: 'wallet', adminOnly: false },
    { text: t('navigation.rewards'), icon: <RewardsIcon />, path: 'rewards', adminOnly: false },
    { text: t('navigation.governance'), icon: <GovernanceIcon />, path: 'governance', adminOnly: false },
    { text: t('navigation.forms'), icon: <FormsIcon />, path: 'forms', adminOnly: false },
    { text: t('navigation.family'), icon: <FamilyIcon />, path: 'family', adminOnly: false },
    { text: t('navigation.infrastructure'), icon: <InfrastructureIcon />, path: 'infrastructure', adminOnly: false },
    { text: t('navigation.storage', 'Sandėliukas'), icon: <StorageIcon />, path: 'storage', adminOnly: false },
    { text: t('navigation.lockers', 'Spintelės'), icon: <LockersNavIcon />, path: 'lockers', adminOnly: false },
    { text: t('navigation.workSchedule'), icon: <WorkScheduleIcon />, path: 'work-schedule', adminOnly: false },
    { text: t('navigation.vacationSchedule'), icon: <BeachAccessIcon />, path: 'vacation-schedule', adminOnly: false },
    { text: t('navigation.mobileSchedule', 'Mobilus grafikas'), icon: <WorkScheduleIcon />, path: 'mobile-schedule', adminOnly: false },
    { text: t('navigation.workPlans', 'Darbų planai'), icon: <EventIcon />, path: 'work-plans', adminOnly: false },
    { text: t('navigation.exhibition'), icon: <ExhibitionIcon />, path: 'exhibition', adminOnly: false },
    {
      text: t('navigation.exhibitionEmployees', 'Darbuotojai'),
      icon: <UsersIcon />,
      path: 'staff-plans',
      adminOnly: false,
      navParent: 'exhibition',
    },
    { text: t('navigation.planetariumas', 'Planetariumas'), icon: <PlanetariumIcon />, path: 'planetariumas', adminOnly: false },
    { text: t('navigation.schemos', 'Procedūrinis'), icon: <SchemaIcon />, path: 'schemos', adminOnly: false },
    { text: t('navigation.pardavimai'), icon: <PardavimaiIcon />, path: 'pardavimai', adminOnly: false },
    { text: t('navigation.kontaktai', 'Kontaktai'), icon: <ContactsIcon />, path: 'kontaktai', adminOnly: false },
    { text: t('navigation.stiklainis'), icon: <StiklainisIcon />, path: 'stiklainis', adminOnly: false },
    { text: t('navigation.sutartys'), icon: <ContractsIcon />, path: 'sutartys', adminOnly: false },
    { text: t('navigation.pointsDistribution'), icon: <PointsDistributionIcon />, path: 'points-distribution', adminOnly: true },
    { text: t('navigation.users'), icon: <UsersIcon />, path: 'users', adminOnly: false },
    { text: t('navigation.zaidimuAikstele', 'Žaidimų aikštelė'), icon: <PlaygroundIcon />, path: 'playground', adminOnly: false },
  ];

  const bottomMenuItems = [
    { text: t('navigation.dokumentacija'), icon: <DocumentIcon />, path: 'dokumentacija', adminOnly: false },
    { text: t('navigation.skaistykla'), icon: <ComplaintIcon />, path: 'skaistykla', adminOnly: false },
    { text: 'Admin', icon: <AdminIcon />, path: 'admin', adminOnly: true },
    { text: t('navigation.profile'), icon: <ProfileIcon />, path: 'profile', adminOnly: false },
  ];

  const renderMenuItems = (items: any[]) => {
    console.log('🎯 Rendering menu items. Visibility loading:', visibilityLoading, 'Visible panels:', visiblePanels);
    
    // While visibility is loading, only show essential panels (dashboard, profile)
    // This prevents flashing of disabled panels
    const essentialPanels = ['dashboard', 'profile'];
    const panelsToCheck = visibilityLoading || visiblePanels.length === 0
      ? essentialPanels
      : visiblePanels;
    
    return items.map((item) => {
      // Special case: Admin panel - show to users with admin permissions
      const isAdminPanel = item.path === 'admin';
      const hasAdminPerms = hasPermission('admin.panel_visibility') || hasPermission('admin.permissions');
      
      // Skip adminOnly check for admin panel if user has permissions
      if (!isAdminPanel || !hasAdminPerms) {
        // Show admin-only items based on permissions
        if (item.adminOnly && !hasPermission('admin.panel_visibility') && !hasPermission('admin.permissions')) {
          return null;
        }
        
        // Show manager-only items - use permission checks instead
        // Note: managerOnly is deprecated - use permission-based checks
        // Sutartys uses contracts.view only (handled in visibility section below)
        if (item.managerOnly) {
          const hasManagerPerms = hasPermission('schedule.manage') || hasPermission('users.view');
          if (!hasManagerPerms) {
            return null;
          }
        }
        
        // Show master admin-only items based on permissions
        if (item.masterAdminOnly && !hasPermission('admin.panel_visibility') && !hasPermission('admin.permissions')) {
          return null;
        }
      }

      // Check panel visibility settings
      // While loading, only show essential panels (dashboard, profile)
      // Once loaded, use fetched visibility
      // Exception: Critical panels should always be visible to users with appropriate permissions
      if (item.path === 'admin' && (hasPermission('admin.panel_visibility') || hasPermission('admin.permissions'))) {
        // Always show admin panel to users with admin permissions or admin role
      } else if (item.path === 'sutartys' && hasPermission('contracts.view')) {
        // Always show contracts panel to users with contracts permission or admin/manager role
      } else if (item.path === 'pardavimai' && hasPermission('pardavimai.view')) {
        // Always show pardavimai panel to users with pardavimai.view permission or admin/manager role
      } else if (item.path === 'skaistykla' && hasPermission('complaints.view')) {
        // Always show skaistykla panel to users with complaints.view permission or admin/manager role
      } else if (item.path === 'work-plans' && hasPermission('workPlans.view')) {
        // Always show work plans panel to users with workPlans.view permission
      } else if (
        item.path === 'staff-plans' &&
        (hasPermission('staff_plans.view') || hasPermission('staff_plans.edit'))
      ) {
        // Sub-item under Ekspozicija — only while parent section is open
        const parentOpen =
          currentPage === 'exhibition' || currentPage === 'staff-plans';
        if (!parentOpen) {
          return null;
        }
      } else if (item.path === 'users' && hasPermission('users.view')) {
        // Always show Users panel to users with users.view permission (privileges system)
      } else if (item.path === 'work-schedule' && hasPermission('schedule.view')) {
        // Always show Darbo grafikas to users with schedule.view permission
      } else if (item.path === 'vacation-schedule' && hasPermission('schedule.manage')) {
        // Always show Atostogų grafikas to users with schedule.manage permission
      } else if (item.path === 'mobile-schedule' && hasPermission('schedule.view')) {
        // Always show Mobilus grafikas to users with schedule.view permission
      } else if (!panelsToCheck.includes(item.path)) {
        console.log(`🚫 Hiding panel ${item.path} - not in visible panels:`, panelsToCheck);
        return null;
      } else {
        console.log(`✅ Showing panel ${item.path}`);
      }
      
      // Special styling for Playground, Kontaktai, Storage, Stiklainis, and Sutartys buttons
      const isPlayground = item.path === 'playground';
      const isKontaktai = item.path === 'kontaktai';
      const isStorage = item.path === 'storage';
      const isStiklainis = item.path === 'stiklainis';
      const isSutartys = item.path === 'sutartys';
      const isNotLaunched = NOT_LAUNCHED_PANELS.has(item.path);
      
      const isNavChild = Boolean(item.navParent);

      return (
        <ListItem key={item.path || item.text} disablePadding>
          <ListItemButton
            selected={!isNotLaunched && currentPage === item.path}
            disabled={isNotLaunched}
            onClick={() => !isNotLaunched && handleNavigation(item.path)}
            sx={{
              ...(isNavChild && {
                pl: 4,
                py: 0.75,
                minHeight: 40,
              }),
              ...(isNotLaunched && {
                color: 'text.disabled',
                opacity: 0.55,
                cursor: 'default',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-disabled': {
                  opacity: 0.55,
                },
                '& .MuiListItemIcon-root': {
                  color: 'text.disabled',
                },
              }),
              ...((isPlayground || isKontaktai) && !isNotLaunched && {
                backgroundColor: '#FFE0B2', // Light orange background
                color: '#E65100', // Dark orange text
                '&:hover': {
                  backgroundColor: '#FFCC80', // Slightly darker orange on hover
                },
                '& .MuiListItemIcon-root': {
                  color: '#E65100', // Dark orange icon
                },
              }),
              ...(isStorage && {
                backgroundColor: '#f3e5f5', // Light purple background
                color: '#7b1fa2', // Purple text
                '&:hover': {
                  backgroundColor: '#e1bee7', // Slightly darker purple on hover
                },
                '& .MuiListItemIcon-root': {
                  color: '#7b1fa2', // Purple icon
                },
              }),
              ...(isStiklainis && {
                backgroundColor: '#e8f5e8', // Light green background
                color: '#2e7d32', // Dark green text
                '&:hover': {
                  backgroundColor: '#c8e6c9', // Slightly darker green on hover
                },
                '& .MuiListItemIcon-root': {
                  color: '#2e7d32', // Dark green icon
                },
              }),
              ...(isSutartys && {
                backgroundColor: '#e3f2fd', // Light blue background
                color: '#1565c0', // Dark blue text
                '&:hover': {
                  backgroundColor: '#bbdefb', // Slightly darker blue on hover
                },
                '& .MuiListItemIcon-root': {
                  color: '#1565c0', // Dark blue icon
                },
              }),
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon>
              {item.path === 'exhibition' && unassignedExcursionCount > 0 ? (
                <Tooltip
                  title={t(
                    'excursions.unassignedNavTooltip',
                    'Yra nepriskirtų ekskursijų ({{count}})',
                    { count: unassignedExcursionCount }
                  )}
                >
                  <Badge badgeContent={unassignedExcursionCount} color="warning" max={99}>
                    {item.icon}
                  </Badge>
                </Tooltip>
              ) : item.path === 'planetariumas' && pendingPlanetariumRequestCount > 0 ? (
                <Tooltip
                  title={t(
                    'planetarium.pendingRequestsNavTooltip',
                    'Yra laukiančių planetariumo prašymų ({{count}})',
                    { count: pendingPlanetariumRequestCount }
                  )}
                >
                  <Badge badgeContent={pendingPlanetariumRequestCount} color="warning" max={99}>
                    {item.icon}
                  </Badge>
                </Tooltip>
              ) : item.path === 'family' && pendingTicketRequestCount > 0 ? (
                <Tooltip
                  title={t(
                    'family.pendingRequestsNavTooltip',
                    'Yra laukiančių bilietų prašymų ({{count}})',
                    { count: pendingTicketRequestCount }
                  )}
                >
                  <Badge badgeContent={pendingTicketRequestCount} color="warning" max={99}>
                    {item.icon}
                  </Badge>
                </Tooltip>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        </ListItem>
      );
    });
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
          Salos Tinklas
        </Typography>
      </Toolbar>
      <Divider />
      
      {/* Main menu items */}
      <List sx={{ flexGrow: 1 }}>
        {renderMenuItems(mainMenuItems)}
      </List>
      
      {/* Bottom menu items */}
      <Box>
        <Divider />
        <List>
          {renderMenuItems(bottomMenuItems)}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
          ml: { md: `${desktopDrawerWidth}px` },
          transition: (thm) =>
            thm.transitions.create(['margin', 'width'], {
              easing: thm.transitions.easing.sharp,
              duration: thm.transitions.duration.enteringScreen
            })
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Salos Tinklas
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LanguageSwitcher />
            <NotificationBell />
            <Typography variant="body2">
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircleIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { md: desktopDrawerWidth },
          flexShrink: { md: 0 },
          transition: (thm) =>
            thm.transitions.create('width', {
              easing: thm.transitions.easing.sharp,
              duration: thm.transitions.duration.enteringScreen
            })
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, height: '100vh' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopDrawerWidth,
              height: '100vh',
              overflowX: 'hidden',
              borderRight: desktopDrawerWidth ? undefined : 'none',
              transition: (thm) =>
                thm.transitions.create('width', {
                  easing: thm.transitions.easing.sharp,
                  duration: thm.transitions.duration.enteringScreen
                })
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Desktop fold / unfold control — high-visibility edge chevron */}
      <IconButton
        onClick={handleNavCollapseToggle}
        aria-label={navCollapsed ? 'Išskleisti meniu' : 'Suskleisti meniu'}
        title={navCollapsed ? 'Išskleisti meniu' : 'Suskleisti meniu'}
        sx={{
          display: { xs: 'none', md: 'inline-flex' },
          position: 'fixed',
          top: 72,
          left: navCollapsed ? 8 : drawerWidth - 20,
          zIndex: (thm) => thm.zIndex.drawer + 2,
          width: 44,
          height: 44,
          bgcolor: 'primary.main',
          color: '#fff',
          border: '2px solid #fff',
          boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
          transition: (thm) =>
            thm.transitions.create(['left', 'background-color'], {
              easing: thm.transitions.easing.sharp,
              duration: thm.transitions.duration.enteringScreen
            }),
          '&:hover': {
            bgcolor: 'primary.dark',
            color: '#fff',
            boxShadow: '0 6px 18px rgba(0,0,0,0.45)'
          }
        }}
      >
        {navCollapsed ? <ChevronRightIcon fontSize="large" /> : <ChevronLeftIcon fontSize="large" />}
      </IconButton>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
          mt: '64px', // Height of AppBar
          transition: (thm) =>
            thm.transitions.create('width', {
              easing: thm.transitions.easing.sharp,
              duration: thm.transitions.duration.enteringScreen
            })
        }}
      >
        {children}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleNavigation('profile')}>
          <ListItemIcon>
            <ProfileIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      
      {/* Floating Documentation Panel */}
      <FloatingDocumentation 
        open={documentationOpen} 
        onClose={() => setDocumentationOpen(false)} 
      />

      <SchedulingCloseAnnouncementModal
        onNavigateToSchedule={() => handleNavigation('work-schedule')}
      />
    </Box>
  );
};

export default Layout; 